import React, { useEffect, useState } from "react";
import placeholder from "./placeholder.jpg";
import { FaCamera, FaCheck, FaTimes, FaSearchPlus, FaSearchMinus } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropimg";

const ProfilePhoto = () => {
  const [photoURL, setPhotoURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMsgVisible, setSuccessMsgVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [imageForCropping, setImageForCropping] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return {};
    }
  })();

  const userId = userData?.id;
  const initialPhotoURL = userData?.profilePhoto || null;

  useEffect(() => {
    setPhotoURL(initialPhotoURL || placeholder);
  }, [initialPhotoURL]);

  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const imageUrl = URL.createObjectURL(file);
      setImageForCropping(imageUrl);
      setShowCropper(true);
    }
  };

  const handleCropConfirm = async () => {
    if (!imageForCropping || !croppedAreaPixels) return;

    setUploading(true);
    setErrorMsg("");
    setSuccessMsgVisible(false);

    try {
      const croppedImageFile = await getCroppedImg(
        imageForCropping,
        croppedAreaPixels
      );

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("photo", croppedImageFile);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload-profile-photo`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await res.json();

      if (res.ok) {
        setPhotoURL(data.photo_url);
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...userData,
            profilePhoto: data.photo_url,
          })
        );
        setSuccessMsgVisible(true);
        setTimeout(() => setSuccessMsgVisible(false), 2500);
      } else {
        setErrorMsg(data.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setErrorMsg("Upload error");
    } finally {
      setShowCropper(false);
      setUploading(false);
      setImageForCropping(null);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 mt-10 relative z-10">
      {/* Cropper Modal */}
      <AnimatePresence>
        {showCropper && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex flex-col items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="relative w-full max-w-md h-96 bg-gray-900 rounded-lg overflow-hidden shadow-xl"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <Cropper
                image={imageForCropping}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
                classes={{
                  containerClassName: "bg-gray-900",
                  mediaClassName: "max-h-full max-w-full object-contain",
                }}
              />
              
              {/* Zoom Controls */}
              <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center px-4">
                <div className="flex items-center gap-4 w-full max-w-xs">
                  <FaSearchMinus className="text-white" />
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(e.target.value)}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <FaSearchPlus className="text-white" />
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setShowCropper(false);
                  setImageForCropping(null);
                }}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-full flex items-center gap-2 transition-colors"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-full flex items-center gap-2 transition-colors"
                disabled={uploading}
              >
                <FaCheck /> {uploading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Photo Container */}
      <div className="relative group w-36 h-36 rounded-full overflow-hidden border-4 border-blue-600 shadow-lg">
        <img
          src={photoURL}
          alt="Profile"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = placeholder;
          }}
        />

        {/* Upload Loader Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <motion.div
              className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            />
          </div>
        )}

        {/* Camera Icon on Hover */}
        <label
          htmlFor="fileInput"
          className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        >
          <FaCamera className="text-white text-2xl" />
        </label>
        <input
          id="fileInput"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Success Message Animation */}
      <AnimatePresence>
        {successMsgVisible && (
          <motion.p
            className="text-green-600 font-medium text-sm"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            Profile photo updated successfully!
          </motion.p>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {errorMsg && (
        <motion.p 
          className="text-red-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {errorMsg}
        </motion.p>
      )}
    </div>
  );
};

export default ProfilePhoto;